import { storedTokens } from '../api/client';

/**
 * Who is signed in, read out of the token we already hold.
 *
 * <p>No request, and that is the point: an auditor opens the app in a stockroom with no
 * signal and the shell still has to draw itself — their name, their menu, the screens they
 * are allowed into. A "who am I" round trip would leave all of that blank in exactly the
 * place the product is supposed to work.</p>
 *
 * <p>This is for drawing, never for deciding. Permissions here hide menu items the user
 * cannot use; the API checks the same permissions on every endpoint and is the only thing
 * standing between a request and the data. A token edited by hand gets a longer menu and
 * exactly the same refusals.</p>
 */
export interface Session {
  userId: string;
  name: string;
  email: string;
  tenant: string;
  permissions: ReadonlySet<string>;
  roles: readonly string[];
}

/** Decodes one base64url segment of a JWT. Returns null rather than throwing on anything odd. */
function payloadOf(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) return null;

  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    // atob gives bytes; the claims are UTF-8, and a Turkish name would otherwise arrive mangled.
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** A claim that may arrive once as a string or many times as an array. */
function many(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  return typeof value === 'string' ? [value] : [];
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

export function session(): Session | null {
  const tokens = storedTokens();
  if (!tokens) return null;

  const claims = payloadOf(tokens.accessToken);
  if (!claims) return null;

  const email = text(claims['email']);

  return {
    userId: text(claims['sub']),
    // A server older than this build has no name claim, and a card reading "?" over an
    // email address looks like the session is broken rather than like the server is behind.
    // The part before the @ is not the person's name, but it is recognisably them.
    name: text(claims['name']) || email.split('@')[0] || '',
    email,
    tenant: text(claims['tenant']),
    permissions: new Set(many(claims['perm'])),
    roles: many(claims['role']),
  };
}

/** No permission required means everyone who is signed in. */
export function allowed(session: Session | null, permission?: string): boolean {
  if (!permission) return true;
  return session?.permissions.has(permission) ?? false;
}
